import classNames from 'classnames';
import React from 'react';
import { ApprovalFlowSummary } from '../../../../containers/applicant_tracking/ApprovalFlows/components/ApprovalFlowSummary';
import { ApprovalFlowStep } from '../../../../entities/applicant_tracking/ApprovalFlow';
import { User } from '../../../../entities/User';
import {
  RequisitionApprover,
  RequisitionApproverAnswer,
} from '../../../../entities/v1/applicant_tracking/Requisition';
import {
  getCurrentStepOrder,
  buildSteps,
} from '../../../../utils/applicant_tracking/requisition';
import { getOrdinal } from '../../../../utils/number';
import { Avatar } from '../../../Avatar';
import { Chip, ChipColor } from '../../../Chip';
import RequisitionCard, {
  REQUISITION_CARD_MIN_HEIGHT,
} from '../RequisitionCard';

interface PropTypes {
  approvers: RequisitionApprover[];
  currentUser: User;
}

function getDeclinedStepOrder(steps: ApprovalFlowStep[]): number {
  return steps.find((step) =>
    step.approvers.find(
      (approver) => approver.answer === RequisitionApproverAnswer.Declined,
    ),
  )?.order;
}

function buildStatusText(steps: ApprovalFlowStep[]): string {
  const wasApprovedByAll = steps.every((step) =>
    step.approvers.find(
      (approver) => approver.answer === RequisitionApproverAnswer.Approved,
    ),
  );

  if (wasApprovedByAll) {
    return 'Approved by all';
  }

  const declinedStepOrder = getDeclinedStepOrder(steps);

  if (declinedStepOrder != null) {
    return null;
  }

  const currentStepOrder = getCurrentStepOrder(steps);

  if (currentStepOrder != null) {
    return `Awaiting for ${getOrdinal(currentStepOrder)} approval`;
  }

  return null;
}

function StatusChip(props: { steps: ApprovalFlowStep[] }) {
  const statusText = buildStatusText(props.steps);

  if (!statusText) {
    return null;
  }

  return (
    <Chip className='fw-semibold' color={ChipColor.Gray}>
      {statusText}
    </Chip>
  );
}

function DeclinedReason(
  props: PropTypes & { declinedApprover: RequisitionApprover },
) {
  const user = props.declinedApprover.user;

  return (
    <div>
      <div className='fs-5 mb-2 mt-2h'>Decline Reason</div>
      <div className='rounded-4 bg-light-danger p-3'>
        <div className='d-flex align-items-center mb-2'>
          <Avatar
            size='lg'
            className='me-2h'
            name={user.photoUrl ?? user.name}
            type={user.photoUrl ? 'photo' : 'initials'}
          />
          <div className='fs-5 fw-semibold text-nowrap text-primary'>
            {user.name} {props.currentUser.id === user.id && '(You)'}
          </div>
        </div>
        <div className='fs-6'>{props.declinedApprover.declineReason}</div>
      </div>
    </div>
  );
}

export default function ApprovalFlowCard(props: PropTypes) {
  const approvalSteps = buildSteps(props.approvers);
  const currentStepOrder = getCurrentStepOrder(approvalSteps);
  const declinedStepOrder = getDeclinedStepOrder(approvalSteps);
  const declinedApprover = props.approvers.find(
    (approver) => approver.answer === RequisitionApproverAnswer.Declined,
  );

  return (
    <RequisitionCard
      title={'Approval Flow'}
      status={<StatusChip steps={approvalSteps} />}
      customStyles={{ minHeight: REQUISITION_CARD_MIN_HEIGHT }}
    >
      <ApprovalFlowSummary
        steps={approvalSteps}
        approversBreakLine={true}
        className={classNames('mt-3 overflow-scroll hide-scrollbar')}
        currentUser={props.currentUser}
        currentStepOrder={currentStepOrder}
        declinedStepOrder={declinedStepOrder}
      />
      {declinedApprover?.declineReason && (
        <DeclinedReason {...props} declinedApprover={declinedApprover} />
      )}
    </RequisitionCard>
  );
}
